import { useMemo, useState } from "react";
import { ConferenceLink } from "./ConferenceLink";
import { useFetch } from "./useFetch";
import { SortHeader, type SortSpec, toggleSort } from "./SortHeader";
import type {
  Conference,
  ConferencesResponse,
  StandingsResponse,
  TeamsResponse,
} from "./types";

type Row = {
  slug: string;
  name: string;
  shortName: string;
  teamCount: number;
  // Average RPI rank across every team in this conference that has
  // a rank in the current standings table. undefined = no ranked
  // teams (new division, no games yet).
  avgRpiRank: number | undefined;
};

type Key = "rank" | "name" | "shortName" | "teamCount" | "avgRpiRank";

const DEFAULT_SORT: SortSpec<Key> = { key: "rank", dir: "asc" };

const DEFAULT_DIR: Record<Key, "asc" | "desc"> = {
  rank: "asc",
  name: "asc",
  shortName: "asc",
  teamCount: "desc",
  avgRpiRank: "asc", // lower average rank is a stronger conference
};

const SPORT_LABEL: Record<string, string> = {
  "soccer-women": "Women's Soccer",
  "soccer-men": "Men's Soccer",
  football: "Football",
  "basketball-women": "Women's Basketball",
  "basketball-men": "Men's Basketball",
};

// Standings slugs are hyphen-cased ("wake-forest"); teams slugs are
// underscore-cased ("wake_forest"). Key the rank lookup by both forms
// so a team row with either style finds its rank.
function buildRankLookup(standings: StandingsResponse | null): Map<string, number> {
  const m = new Map<string, number>();
  if (!standings) return m;
  for (const s of standings.standings) {
    m.set(s.teamSlug, s.rank);
    m.set(s.teamSlug.replace(/-/g, "_"), s.rank);
  }
  return m;
}

export type ConferencesPageProps = {
  sport: string;
  division: string;
};

export function ConferencesPage({ sport, division }: ConferencesPageProps) {
  const [sort, setSort] = useState<SortSpec<Key>>(DEFAULT_SORT);

  // Conferences list is always-full (not sport-specific); fetched
  // once. Teams drives the per-conference membership + count.
  // Standings drives the Avg RPI Rank per conference.
  const confs = useFetch<ConferencesResponse>("/api/v1/conferences");
  const teams = useFetch<TeamsResponse>(
    `/api/v1/teams?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`,
  );
  const standings = useFetch<StandingsResponse>(
    `/api/v1/standings?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`,
  );

  // Build rows. Avg RPI rank is the mean of each member team's rank,
  // skipping teams that aren't in the standings table (teams with no
  // ranked games yet). Conferences whose teams are all unranked show
  // undefined and sort to the bottom of the Rank column.
  const unsortedRows = useMemo<Row[]>(() => {
    if (confs.kind !== "ok" || teams.kind !== "ok") return [];
    const rankBySlug = buildRankLookup(
      standings.kind === "ok" ? standings.data : null,
    );
    const counts = new Map<string, number>();
    const rankSum = new Map<string, number>();
    const rankCount = new Map<string, number>();
    for (const t of teams.data.teams) {
      counts.set(t.conference, (counts.get(t.conference) ?? 0) + 1);
      const r = rankBySlug.get(t.slug) ?? rankBySlug.get(t.slug.replace(/_/g, "-"));
      if (r !== undefined) {
        rankSum.set(t.conference, (rankSum.get(t.conference) ?? 0) + r);
        rankCount.set(t.conference, (rankCount.get(t.conference) ?? 0) + 1);
      }
    }
    const out: Row[] = [];
    for (const c of confs.data.conferences as Conference[]) {
      const n = counts.get(c.slug) ?? 0;
      if (n === 0) continue;
      const rc = rankCount.get(c.slug) ?? 0;
      const avg = rc > 0 ? (rankSum.get(c.slug) ?? 0) / rc : undefined;
      out.push({
        slug: c.slug,
        name: c.name,
        shortName: c.shortName,
        teamCount: n,
        avgRpiRank: avg,
      });
    }
    return out;
  }, [confs, teams, standings]);

  // Compute Rank based on the Avg RPI Rank ordering. Done before the
  // user-selectable sort so the Rank value is stable regardless of
  // how the user has clicked the headers. Unranked conferences
  // (avgRpiRank undefined) share the trailing rank slots.
  const rankedRows = useMemo(() => {
    const sorted = [...unsortedRows].sort((a, b) => {
      if (a.avgRpiRank === undefined && b.avgRpiRank === undefined) return 0;
      if (a.avgRpiRank === undefined) return 1;
      if (b.avgRpiRank === undefined) return -1;
      return a.avgRpiRank - b.avgRpiRank;
    });
    const byConfSlug = new Map<string, number>();
    sorted.forEach((r, i) => byConfSlug.set(r.slug, i + 1));
    return unsortedRows.map((r) => ({
      ...r,
      rank: byConfSlug.get(r.slug) ?? 0,
    }));
  }, [unsortedRows]);

  const sorted = useMemo(() => sortRows(rankedRows, sort), [rankedRows, sort]);

  const head = (
    <>
      <div className="page-head">
        <h2>
          Conferences ·{" "}
          <span className="muted">
            {division.toUpperCase()} {SPORT_LABEL[sport] ?? sport}
          </span>
        </h2>
        {sorted.length > 0 && (
          <span
            className="count-badge"
            role="status"
            aria-label={`${sorted.length} ${sorted.length === 1 ? "conference" : "conferences"} with ${division.toUpperCase()} ${SPORT_LABEL[sport] ?? sport} teams`}
          >
            {sorted.length} {sorted.length === 1 ? "conference" : "conferences"}
          </span>
        )}
      </div>
    </>
  );

  if (confs.kind === "error") {
    return (
      <section>
        {head}
        <p role="alert">Failed to load conferences: {confs.message}</p>
      </section>
    );
  }
  if (teams.kind === "error") {
    return (
      <section>
        {head}
        <p role="alert">
          Failed to load teams (needed for conference counts): {teams.message}
        </p>
      </section>
    );
  }
  if (confs.kind === "loading" || teams.kind === "loading") {
    return (
      <section>
        {head}
        <p>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      {head}
      {sorted.length === 0 ? (
        <p className="empty">
          No conferences with {division.toUpperCase()}{" "}
          {SPORT_LABEL[sport] ?? sport} teams yet.
        </p>
      ) : (
        <div className="table-scroll">
          <ConferencesTable
            rows={sorted}
            sort={sort}
            onSort={(k) => setSort(toggleSort(sort, k, DEFAULT_DIR[k]))}
            teamCountTitle={`${SPORT_LABEL[sport] ?? sport} ${division.toUpperCase()} teams in this conference`}
            sport={sport}
            division={division}
          />
        </div>
      )}
    </section>
  );
}

type RankedRow = Row & { rank: number };

function ConferencesTable({
  rows,
  sort,
  onSort,
  teamCountTitle,
  sport,
  division,
}: {
  rows: RankedRow[];
  sort: SortSpec<Key>;
  onSort: (k: Key) => void;
  teamCountTitle: string;
  sport: string;
  division: string;
}) {
  return (
    <table className="standings">
      <thead>
        <tr>
          <SortHeader
            label="Rank"
            sortKey="rank"
            sort={sort}
            onToggle={onSort}
            align="right"
            title="Rank among conferences by average RPI rank of member teams"
          />
          <SortHeader
            label="Short"
            sortKey="shortName"
            sort={sort}
            onToggle={onSort}
          />
          <SortHeader
            label="Long"
            sortKey="name"
            sort={sort}
            onToggle={onSort}
          />
          <SortHeader
            label="Teams"
            sortKey="teamCount"
            sort={sort}
            onToggle={onSort}
            align="right"
            title={teamCountTitle}
          />
          <SortHeader
            label="Avg RPI Rank"
            sortKey="avgRpiRank"
            sort={sort}
            onToggle={onSort}
            align="right"
            title="Mean RPI rank across this conference's teams. Lower is stronger."
          />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.slug}>
            <td className="num">{r.rank}</td>
            <td>
              <ConferenceLink
                conference={r.slug}
                sport={sport}
                division={division}
                label={r.shortName}
                title={`Show ${r.name} standings`}
              />
            </td>
            <td>
              <ConferenceLink
                conference={r.slug}
                sport={sport}
                division={division}
                label={r.name}
              />
            </td>
            <td className="num">{r.teamCount}</td>
            <td className="num">
              {r.avgRpiRank === undefined ? "—" : r.avgRpiRank.toFixed(1)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function sortRows(rows: RankedRow[], sort: SortSpec<Key>): RankedRow[] {
  const dir = sort.dir === "asc" ? 1 : -1;
  const key = sort.key;
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    // Push undefined to the end regardless of direction — undefined
    // sort to the bottom of asc and the top of desc otherwise, which
    // is confusing for "worst-rank" semantics.
    if (av === undefined && bv === undefined) return 0;
    if (av === undefined) return 1;
    if (bv === undefined) return -1;
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv)) * dir;
  });
}
